import exifr from "exifr";

const parseExifDate = (value) => {
  if (!value) return null;

  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  if (typeof value === "number") {
    const dt = new Date(value);
    return Number.isNaN(dt.getTime()) ? null : dt;
  }

  if (typeof value !== "string") return null;

  const exifStyle = value.match(/^(\d{4}):(\d{2}):(\d{2}) (\d{2}):(\d{2}):(\d{2})$/);
  if (exifStyle) {
    const [, y, mo, d, h, mi, s] = exifStyle;
    const dt = new Date(
      Number(y),
      Number(mo) - 1,
      Number(d),
      Number(h),
      Number(mi),
      Number(s)
    );
    return Number.isNaN(dt.getTime()) ? null : dt;
  }

  // Some files store ISO-like values in metadata fields.
  const normalized = value.trim().replace(/([+-]\d{2})(\d{2})$/, "$1:$2");
  const isoDate = new Date(normalized);
  return Number.isNaN(isoDate.getTime()) ? null : isoDate;
};

const getImageEmbeddedDate = async (file) => {
  const isImage = /^image\//i.test(file?.type || "") ||
    /\.(jpe?g|png|webp|heic|heif|tif|tiff|bmp|gif|avif)$/i.test(file?.name || "");
  if (!isImage) return null;

  try {
    const tags = await exifr.parse(file, {
      // Prefer capture-related tags first.
      pick: [
        "DateTimeOriginal",
        "CreateDate",
        "DateTimeDigitized",
        "ModifyDate",
        "DateTime",
        "CreationDate",
      ],
      tiff: true,
      ifd0: true,
      exif: true,
      xmp: true,
      icc: false,
      iptc: false,
      gps: false,
    });

    return (
      parseExifDate(tags?.DateTimeOriginal) ||
      parseExifDate(tags?.CreateDate) ||
      parseExifDate(tags?.DateTimeDigitized) ||
      parseExifDate(tags?.ModifyDate) ||
      parseExifDate(tags?.DateTime) ||
      parseExifDate(tags?.CreationDate)
    );
  } catch {
    return null;
  }
};

const resolveFileDate = async (file, fallbackDate) => {
  const exifDate = await getImageEmbeddedDate(file);
  if (exifDate) return exifDate;

  if (file?.lastModified && Number.isFinite(file.lastModified)) {
    const lastModifiedDate = new Date(file.lastModified);
    if (!Number.isNaN(lastModifiedDate.getTime())) return lastModifiedDate;
  }

  return fallbackDate;
};

const toDotNetFormDateTime = (date) => {
  const pad = (n) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
};

export const toDotNetFormDateTimeNow = () => toDotNetFormDateTime(new Date());

export const resolveMetaDateTime = async (file, fallbackDate = new Date()) => {
  const resolved = await resolveFileDate(file, fallbackDate);
  return toDotNetFormDateTime(resolved);
};

export const resolveMetaDateTimes = async (files, fallbackDate = new Date()) => {
  const list = Array.from(files || []);
  if (list.length === 0) return [];

  const resolved = await Promise.all(list.map((file) => resolveFileDate(file, fallbackDate)));
  return resolved.map((dt) => toDotNetFormDateTime(dt));
};
